import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  createJobRequest,
  getEmploymentTypes,
  getExperienceTypes,
  getJobs,
  removeJob,
  updateJobRequest,
} from "../../../../services/api/landing/careers/jobs/jobsApi";

const getLang = () =>
  localStorage.getItem("language") === "ar" ? "ar" : "en";

const toastMessages = {
  delete: {
    en: "Job deleted successfully!",
    ar: "تم حذف الوظيفة بنجاح!",
  },
  deleteError: {
    en: "Failed to delete job.",
    ar: "فشل في حذف الوظيفة.",
  },
  create: {
    en: "Job created successfully!",
    ar: "تم إنشاء الوظيفة بنجاح!",
  },
  createError: {
    en: "Failed to create job.",
    ar: "فشل في إنشاء الوظيفة.",
  },
  update: {
    en: "Job updated successfully!",
    ar: "تم تحديث الوظيفة بنجاح!",
  },
  updateError: {
    en: "Failed to update job.",
    ar: "فشل في تحديث الوظيفة.",
  },
};

export const fetchJobs = createAsyncThunk(
  "jobs/fetchJobs",
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const response = await getJobs(dateRange);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch jobs");
    }
  },
);

export const fetchExperienceTypes = createAsyncThunk(
  "jobs/fetchExperienceTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getExperienceTypes();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch experience types");
    }
  },
);

export const fetchEmploymentTypes = createAsyncThunk(
  "jobs/fetchEmploymentTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getEmploymentTypes();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch employment types");
    }
  },
);

export const deleteJob = createAsyncThunk(
  "jobs/deleteJob",
  async (jobId, { rejectWithValue }) => {
    try {
      await removeJob(jobId);
      return jobId;
    } catch (error) {
      return rejectWithValue("Failed to delete job");
    }
  },
);

export const createJob = createAsyncThunk(
  "jobs/createJob",
  async (jobData, { rejectWithValue }) => {
    try {
      const response = await createJobRequest(jobData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to create job");
    }
  },
);

export const updateJob = createAsyncThunk(
  "jobs/updateJob",
  async ({ jobId, jobData }, { rejectWithValue }) => {
    try {
      const response = await updateJobRequest(jobId, jobData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to update job");
    }
  },
);

const jobSlice = createSlice({
  name: "jobs",
  initialState: {
    jobData: [],
    experienceTypes: [],
    employmentTypes: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.jobData = action.payload;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchExperienceTypes.fulfilled, (state, action) => {
        state.experienceTypes = action.payload;
      })
      .addCase(fetchExperienceTypes.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchEmploymentTypes.fulfilled, (state, action) => {
        state.employmentTypes = action.payload;
      })
      .addCase(fetchEmploymentTypes.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.jobData = state.jobData.filter((job) => job.id !== action.payload);
        toast.success(toastMessages.delete[getLang()]);
      })
      .addCase(deleteJob.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.deleteError[getLang()]);
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.jobData.push(action.payload);
        toast.success(toastMessages.create[getLang()]);
      })
      .addCase(createJob.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.createError[getLang()]);
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        const index = state.jobData.findIndex(
          (job) => job.id === action.payload.id,
        );
        if (index !== -1) {
          state.jobData[index] = action.payload;
        }
        toast.success(toastMessages.update[getLang()]);
      })
      .addCase(updateJob.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(toastMessages.updateError[getLang()]);
      });
  },
});

export default jobSlice.reducer;
